import React from 'react';
import { useTranslation } from 'react-i18next';
import { AttachmentPreview } from '@/components/AttachmentPreview';
import { AutoHideScrollArea } from '@/components/AutoHideScrollArea';
import { EmptyState } from '@/components/EmptyState';
import { EvaluationCard } from '@/components/LessonDetailsModal/EvaluationCard';
import { HomeTaskView } from '@/components/HomeTaskModal/HomeTaskView';
import { EvaluationHistoryTable } from '@/components/LessonDetailsModal/EvaluationHistoryTable';
import {
  useLessonDetailsModal,
  type LessonDetailsParams,
} from '@/components/LessonDetailsModal/hooks/useLessonDetailsModal';
import { LessonDetailsHeader } from '@/components/LessonDetailsModal/LessonDetailsHeader';
import { LessonDetailsSection } from '@/components/LessonDetailsModal/LessonDetailsSection';
import { LessonHomeworkCards } from '@/components/LessonDetailsModal/LessonHomeworkCards';
import { LessonTopic } from '@/components/LessonDetailsModal/LessonTopic';
import { LessonVideoCard } from '@/components/LessonDetailsModal/LessonVideoCard';
import { MobileLessonTitle } from '@/components/LessonDetailsModal/MobileLessonTitle';
import { TestReportView } from '@/components/LessonDetailsModal/TestReportView';
import { Modal } from '@/components/Modal';
import { ModalHeader } from '@/components/Modal/ModalHeader';
import { PageSpinner } from '@/components/Spinner/PageSpinner';
import { tv } from '@/utils/tv';

const SUB_VIEW_CLASS =
  'absolute inset-0 z-10 flex flex-col bg-background animate-zoom-in';

const modal = tv({
  base: 'md:h-[788px] md:max-h-[85svh] md:w-[min(853px,calc(100vw-40px))]',
  variants: {
    isBottomSheet: { true: 'max-md:max-h-[90svh]' },
  },
});

const sheetBody = tv({
  variants: {
    isAnimated: {
      true: 'flex min-h-0 flex-col overflow-hidden transition-[height] duration-200 ease-out',
      false: 'contents',
    },
  },
});

const content = tv({
  base: 'flex flex-col gap-5 px-4 pb-[calc(24px+env(safe-area-inset-bottom))] md:px-5 md:pb-6',
  variants: {
    isBottomSheet: { true: 'pt-2 md:pt-4', false: 'md:pt-3' },
  },
});

type Props = LessonDetailsParams;

export const LessonDetailsModal: React.FC<Props> = (props) => {
  const { onClose } = props;
  const { t } = useTranslation();
  const {
    lesson,
    evaluation,
    heading,
    videoMeetings,
    isScrolled,
    isContentReady,
    isNotFound,
    isBottomSheet,
    isAnimatedSheet,
    isSheetClosing,
    isReturningFromSubView,
    modalVariant,
    sheetBodyStyle,
    sheetRef,
    swipeProps,
    contentRef,
    handleScroll,
    testReportId,
    homeTaskId,
    previewAttachment,
    setPreviewAttachment,
    openTestReport,
    openHomeTask,
    closeTestReport,
    closeHomeTask,
  } = useLessonDetailsModal(props);

  if (isAnimatedSheet && testReportId !== null) {
    return (
      <Modal
        key="test-report"
        isOpen
        label={t('diary.testReport.title')}
        onClose={onClose}
      >
        <TestReportView
          testId={testReportId}
          onBack={onClose}
          onClose={onClose}
        />
      </Modal>
    );
  }

  return (
    <Modal
      key="lesson-details"
      isOpen={!isSheetClosing}
      label={heading?.title ?? t('diary.evaluation.title')}
      variant={modalVariant}
      className={modal({ isBottomSheet })}
      contentRef={isBottomSheet ? sheetRef : undefined}
      onClose={onClose}
    >
      <div className="relative flex min-h-0 flex-1 flex-col">
        <div
          className={`flex min-h-0 flex-1 flex-col ${isReturningFromSubView ? 'animate-zoom-in' : ''}`}
        >
          {isBottomSheet && (
            <div
              {...swipeProps}
              className="flex shrink-0 cursor-grab touch-none justify-center py-2 active:cursor-grabbing md:hidden"
            >
              <div className="h-[3px] w-10 rounded-full bg-drag-handle" />
            </div>
          )}
          {!isBottomSheet && (
            <div className="md:hidden">
              <ModalHeader
                isMobileTitleHidden={!isScrolled}
                title={heading?.title ?? ''}
                onClose={onClose}
              />
            </div>
          )}
          <LessonDetailsHeader
            heading={heading}
            hasVideoMeeting={videoMeetings.length > 0}
            onClose={onClose}
          />
          {isBottomSheet && !!heading && (
            <div className="shrink-0 px-4 pb-3 md:hidden">
              <MobileLessonTitle isEvaluation heading={heading} />
            </div>
          )}

          <div
            style={sheetBodyStyle}
            className={sheetBody({ isAnimated: isAnimatedSheet })}
          >
            {!isContentReady && (
              <div className="flex flex-1 items-center justify-center max-md:min-h-40">
                <PageSpinner className="size-[22px] text-subtle-foreground" />
              </div>
            )}

            {isNotFound && (
              <EmptyState label={t('diary.evaluation.notFound')} />
            )}

            {isContentReady && !!heading && (
              <AutoHideScrollArea
                hasTopShadow
                className="flex min-h-0 flex-1 flex-col"
                onScroll={handleScroll}
              >
                <div ref={contentRef} className={content({ isBottomSheet })}>
                  {!isBottomSheet && <MobileLessonTitle heading={heading} />}

                  {videoMeetings.map((meeting) => (
                    <LessonVideoCard key={meeting.id} meeting={meeting} />
                  ))}

                  <LessonTopic
                    lesson={lesson}
                    evaluation={evaluation}
                    onPreview={setPreviewAttachment}
                  />

                  {evaluation && (
                    <LessonDetailsSection title={t('diary.evaluation.title')}>
                      <EvaluationCard
                        detail={evaluation}
                        onOpenTestReport={openTestReport}
                      />
                    </LessonDetailsSection>
                  )}

                  {!!evaluation?.editedEvaluations.length && (
                    <LessonDetailsSection
                      title={t('diary.evaluation.historyTitle')}
                    >
                      <EvaluationHistoryTable
                        lessonTypeId={evaluation.lesson.type.lessonTypeId}
                        edits={evaluation.editedEvaluations}
                      />
                    </LessonDetailsSection>
                  )}

                  {lesson && (
                    <LessonDetailsSection title={t('diary.table.homework')}>
                      <LessonHomeworkCards
                        lesson={lesson}
                        onPreview={setPreviewAttachment}
                        onOpenHomeTask={openHomeTask}
                      />
                    </LessonDetailsSection>
                  )}
                </div>
              </AutoHideScrollArea>
            )}
          </div>
        </div>

        {testReportId !== null && (
          <div className={SUB_VIEW_CLASS}>
            <TestReportView
              testId={testReportId}
              onBack={closeTestReport}
              onClose={onClose}
            />
          </div>
        )}

        {homeTaskId !== null && (
          <div className={SUB_VIEW_CLASS}>
            <HomeTaskView
              homeTaskId={homeTaskId}
              onBack={closeHomeTask}
              onClose={onClose}
            />
          </div>
        )}

        {previewAttachment && (
          <AttachmentPreview
            attachment={previewAttachment}
            onClose={() => setPreviewAttachment(null)}
            onDownloaded={() => setPreviewAttachment(null)}
          />
        )}
      </div>
    </Modal>
  );
};
